import { MSG_TYPES, PROTOCOL_VERSION, ROOM_STATUS } from '../../../shared/protocol.js';
import { isTycoonAvatarId } from '../../../shared/tycoon-avatars.js';
import { queryOne } from '../../core/dom.js';
import { renderTycoonAvatarCustomizer } from './view.js';

export class TycoonAvatarCoordinator {
  constructor({
    controller,
    deviceId,
    context,
    send,
    persistLocal,
    renderGame,
    renderLobby
  }) {
    this.controller = controller;
    this.deviceId = deviceId;
    this.context = context;
    this.send = send;
    this.persistLocal = persistLocal;
    this.renderGame = renderGame;
    this.renderLobby = renderLobby;
    this.editIndex = 0;
    this.bind();
  }

  resetLocalSelection() {
    this.editIndex = 0;
  }

  renderLocal(game, hidden = false) {
    if (!game) return;
    this.editIndex = Math.max(0, Math.min(this.editIndex, game.players.length - 1));
    this.editIndex = renderTycoonAvatarCustomizer({
      game,
      selectedPlayerIndex: this.editIndex,
      hidden
    });
  }

  setLocalAvatar(avatarId) {
    const { game, mode } = this.context();
    if (
      mode === 'online' ||
      !game ||
      !isTycoonAvatarId(avatarId) ||
      !game.players[this.editIndex]
    ) return false;

    const player = game.players[this.editIndex];
    if (player.avatarId === avatarId) return true;

    const otherIndex = game.players.findIndex((item, index) =>
      index !== this.editIndex && item.avatarId === avatarId
    );
    const previousAvatarId = player.avatarId;
    player.avatarId = avatarId;
    if (otherIndex >= 0) game.players[otherIndex].avatarId = previousAvatarId;

    this.persistLocal();
    return true;
  }

  setOnlineAvatar(avatarId) {
    const { state, seat: userSeat } = this.context();
    if (
      !isTycoonAvatarId(avatarId) ||
      !Number.isInteger(userSeat) ||
      state?.status !== ROOM_STATUS.WAITING ||
      !this.controller.beginAvatarUpdate(avatarId)
    ) return false;

    this.renderLobby();
    const sent = this.send({
      type: MSG_TYPES.SET_TYCOON_AVATAR,
      protocolVersion: PROTOCOL_VERSION,
      avatarId,
      deviceId: this.deviceId
    });

    if (!sent) {
      this.controller.settleAvatarUpdate();
      this.renderLobby();
      const note = queryOne('#tycoonLobbyAvatarNote');
      if (note) note.textContent = 'Mất kết nối tới phòng. Hãy thử lại trước khi đổi nhân vật.';
      return false;
    }
    return true;
  }

  handleServerMessage(msg) {
    if (msg?.type === MSG_TYPES.TYCOON_AVATAR_ACCEPTED) {
      this.controller.settleAvatarUpdate();
      this.renderLobby();
      return true;
    }
    if (msg?.type === MSG_TYPES.TYCOON_AVATAR_REJECTED) {
      this.controller.settleAvatarUpdate();
      this.renderLobby();
      const note = queryOne('#tycoonLobbyAvatarNote');
      if (note) note.textContent = msg.reason || 'Không thể đổi nhân vật.';
      return true;
    }
    return false;
  }

  bind() {
    queryOne('#tycoonAvatarPlayerSelect')?.addEventListener('change', event => {
      const { game } = this.context();
      const index = Number(event.target.value);
      if (!game || !Number.isInteger(index) || index < 0 || index >= game.players.length) return;
      this.editIndex = index;
      this.renderGame();
    });

    queryOne('#tycoonLocalAvatarOptions')?.addEventListener('click', event => {
      const button = event.target.closest?.('[data-tycoon-local-avatar]');
      if (!button || button.disabled) return;
      this.setLocalAvatar(button.dataset.tycoonLocalAvatar);
    });

    queryOne('#tycoonLobbyAvatarOptions')?.addEventListener('click', event => {
      const button = event.target.closest?.('[data-tycoon-online-avatar]');
      if (!button || button.disabled) return;
      this.setOnlineAvatar(button.dataset.tycoonOnlineAvatar);
    });
  }
}
